import React from 'react';
import { Percent, MapPin, Building2 } from 'lucide-react';

/**
 * Секция формы для настройки налоговых ставок
 */
const TaxRateParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  taxRegions,
  specialZoneTypes
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <h3 className="col-span-full text-lg font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
        <Percent className="w-5 h-5 text-indigo-500" />
        Налоговый период и ставки
      </h3>
      
      {/* Налоговый период */}
      <div>
        <label htmlFor="taxationYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Налоговый период (год)
        </label>
        <input
          type="text"
          id="taxationYear"
          name="taxationYear"
          value={formData.taxationYear}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.taxationYear ? 'input-error' : ''}`}
          placeholder="Введите год"
        />
        {errors.taxationYear && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.taxationYear}
          </p>
        )}
      </div>

      {/* Регион для определения ставки */}
      <div>
        <label htmlFor="taxRegion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <MapPin className="w-4 h-4 mr-1 text-indigo-500" />
          Регион
        </label>
        <select
          id="taxRegion"
          name="taxRegion"
          value={formData.taxRegion}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.taxRegion ? 'input-error' : ''}`}
        >
          {taxRegions.map((region) => (
            <option key={region.id} value={region.id}>
              {region.name} ({region.regionalRate}%)
            </option>
          ))}
        </select>
        {errors.taxRegion && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.taxRegion}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Выберите регион для определения региональной части ставки налога
        </p>
      </div>

      {/* Специальные налоговые режимы */}
      <div className="col-span-full mt-2">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="specialTaxRegimes"
            name="specialTaxRegimes"
            checked={formData.specialTaxRegimes}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="specialTaxRegimes" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Применяются специальные налоговые режимы и льготы
          </label>
        </div>
      </div>

      {/* IT-компания */}
      {formData.specialTaxRegimes && (
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="isITCompany"
              name="isITCompany"
              checked={formData.isITCompany}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="isITCompany" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Building2 className="w-4 h-4 mr-1 text-indigo-500" />
              IT-компания (льготная ставка)
            </label>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 pl-6">
            Для компаний, аккредитованных в сфере IT, действуют льготные ставки: 3% в федеральный бюджет, 0% в региональный
          </p>
        </div>
      )}

      {/* Резидент особой экономической зоны */}
      {formData.specialTaxRegimes && (
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="isResidentSpecialZone"
              name="isResidentSpecialZone"
              checked={formData.isResidentSpecialZone}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="isResidentSpecialZone" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <MapPin className="w-4 h-4 mr-1 text-indigo-500" />
              Резидент особой экономической зоны
            </label>
          </div>
          
          {formData.isResidentSpecialZone && (
            <div className="mt-2 pl-6">
              <label htmlFor="specialZoneType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Тип особой экономической зоны
              </label>
              <select
                id="specialZoneType"
                name="specialZoneType"
                value={formData.specialZoneType}
                onChange={handleInputChange}
                className="form-select w-full"
              >
                {specialZoneTypes.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name} {zone.taxRate !== undefined ? `(ставка ${zone.taxRate}%)` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                В зависимости от типа ОЭЗ применяются различные региональные ставки налога
              </p>
            </div>
          )}
        </div>
      )}

      {/* Информационный блок */}
      <div className="col-span-full mt-2">
        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
          <div className="flex items-start">
            <Percent className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-3 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-1">
                О ставках налога на прибыль
              </h4>
              <p className="text-xs text-indigo-600 dark:text-indigo-400">
                Базовая ставка налога на прибыль составляет 20%: 3% зачисляется в федеральный бюджет, 17% в бюджет субъекта РФ (ст. 284 НК РФ). Региональные власти могут снижать ставку до 12,5% для отдельных категорий налогоплательщиков. Для IT-компаний, соответствующих критериям ст. 284 НК РФ, действуют льготные ставки. Для резидентов особых экономических зон также предусмотрены льготные налоговые режимы.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxRateParametersSection;